
export const text = {
  appTitle: 'br-esri-boilerplate',
  customShape: 'Analyze',
  firstInstruction: 'Select an Administrative Boundary from the Map Layers',
  instructionBreak: 'or',
  secondInstruction:
    'Analyze your own location by drawing a shape or uploading a zipped shapefile of your AOI (up to 5 million acres)',
  instructionTitle: 'Generate Plowprint Report for an Area of Interest',
  instructionButton: 'Custom Area',
  errorLargeGeometry:
    'Custom areas must be less than 5 million acres.',
  errorNoGeometry:
    'Please select an Administrative Boundary from the Map Layers, draw a shape, or upload a zipped shapefile of your AOI to Generate a Plowprint Report.',
  errorLargeGeometryLog:
    'Your geometry is likely too large. There was an error processing your request: ',
  errorFileUpload:
    'Please ensure that zipped shapefile contains all necessary files and includes a single polygon.'
};

export const emailSignupUrl =
  'https://services1.arcgis.com/RTK5Unh1Z71JKIiR/ArcGIS/rest/services/PlowprintEmails/FeatureServer/0';

// UPDATE THIS!!!
export const yearsAvailableForAnalysis = [
  2012,
  2013,
  2014,
  2015,
  2016,
  2017,
  2018,
  2019,
  2020
];

export const yearsForDisplay = {
  max: 2020,
  min: 2009
};

export const citationText = {
  title: 'Citations:',
  line1: 'Gage, A. M., Olimb, S. K. & Nelson, J. 2016. Plowprint: Tracking cumulative expansion to target grassland',
  subline1: 'conservation. Great Plains Research, 26, 107-116',
  line2: 'Olimb, S. K., & Robinson, B. 2019. Grass to grain: Probabilistic modeling of agricultural conversion in the North', //! update
  subline2: 'American Great Plains. Ecological Indicators, 102, 237-245'
};

export const chartColorScheme = {
  Alfalfa: '#417505',
  'Total Acreage': '#000080',
  'Other Crops': '#9E7F33',
  'Barren/Developed': '#A83801',
  Canola: '#F8C43B',
  Corn: '#E5894B',
  'Cumulative Plowprint': '#5F3B02',
  'Crop/Fallow': '#FEAA02',
  developed: '#FEFC03',
  'Federal Land': '#6303CF',
  Forest: '#57FF01',
  Perennial: '#C500FF',
  Grassland: '#85D300',
  High: '#F7A22C',
  'Intact Extent': '#657746',
  Low: '#2E6842',
  Moderate: '#F7EF75',
  Other: '#885590',
  'Other Grains': '#B8E986',
  private: '#B4D79E',
  Soy: '#79B158',
  'State Land': '#894365',
  Shrubland: '#7E8C5F',
  Tribal: '#FEAA02',
  water: '#015BE7',
  Wetland: '#00C5FF',
  'Very High': '#9A2F27',
  Wheat: '#A0572E'
};

export const innerDonutSeriesColors = [
  // 5 for Intact taken from https://www.color-hex.com/color/cf5c36
  '#85D300',
  '#57FE01',
  '#9AA582',
  '#48C6FF',
  '#885590',
  // 4 for Plowprint taken from https://www.color-hex.com/color/4ca64c
  '#C500FF',
  '#F7AA05',
  '#e46339',
  '#c57358',
  '#015BE7', // water
  '#FEFC03' // developed
];

export const legendIndexForDonutChart = {
  'Grassland (Intact)': 2,
  'Perennial (Plowprint)': 3,
  'Forest (Intact)': 4,
  'Forest (Plowprint)': 5,
  'Shrubland (Intact)': 6,
  'Barren/Developed (Plowprint)': 7,
  'Wetland (Intact)': 8,
  'Crop/Fallow (Plowprint)': 9,
  'Other (Intact)': 10,
  'Open Water': 11,
  'Developed Land': 13 // See documentation for 'indexOfDevelopedLand' in `reportCharts.js`
};

export const reportCharts = {
  plowprintCompositionAndOwnership: {
    title: 'Plowprint Extent by Composition',
    id: 'plowprintCompositionOwnership',
    domContainer: 'plowprintCompositionAndOwnership',
    category: 'columnAndSpline',
    years: yearsAvailableForAnalysis,
    histogramReferences: ['Plowprint Composition', 'Cumulative Plowprint'],
    leftYAxisLegend: 'Plowprint Extent (%)',
    rightYAxisLegend: 'Plowprint Extent (ha)'
  },
  intactCompositionAndOwnership: {
    title: 'Intact Extent by Composition',
    id: 'intactCompositionOwnership',
    years: yearsAvailableForAnalysis,
    domContainer: 'intactCompositionAndOwnership',
    category: 'columnAndSpline',
    histogramReferences: ['Intact Habitat', 'Intact Extent'],
    leftYAxisLegend: 'Intact Extent (%)',
    rightYAxisLegend: 'Intact Extent (ha)'
  },
  plowprintExpansion: {
    title: 'Plowprint Expansion',
    id: 'plowprintExpansion',
    domContainer: 'plowprintExpansion',
    years: yearsAvailableForAnalysis,
    category: 'column',
    histogramReferences: ['Plowprint Annual Expansion'],
    leftYAxisLegend: 'Total Land Cover (%)',
    rightYAxisLegend: null
  },
  openWater: {
    title: 'Plowprint Extent by Composition',
    id: 'openWater',
    domContainer: 'openWater',
    years: yearsAvailableForAnalysis,
    category: 'donut',
    histogramReferences: [
      'Intact Extent',
      'Open Water',
      'Plowprint Composition'
    ],
    leftYAxisLegend: 'Total Land Cover',
    rightYAxisLegend: null
  },
  soilQuality: {
    title: 'Soil Quality and Conversion Risk',
    id: 'soilQuality',
    domContainer: 'soilQuality',
    years: yearsAvailableForAnalysis,
    category: 'bar',
    histogramReferences: ['Plowprint Soil Quality', 'Intact Soil Quality'],
    leftYAxisLegend: 'Percentage %',
    rightYAxisLegend: null
  }
};

export const getCSVKey = key => {
  const maxDate = new Date().getFullYear() - 1;

  const keyOptions = {
    'CumulativePlowprint': 'Cumulative Plowprint',
    'IntactArea': 'Intact Extent',
    'IntactHabitat': 'Intact Habitat',
    'IntactOwnership': 'Intact Ownership',
    'IntactSoilQuality': 'Intact Soil Quality',
    'PlowprintSoilQuality': 'Plowprint Soil Quality',
    'PlowprintExpansionCumulative': `Plowprint Expansion 2011 - ${maxDate}`,
    'PlowprintComposition': 'Plowprint Composition',
    'PlowprintOwnership': 'Plowprint Ownership',
    'OpenWater': 'Open Water',
    'Developed': 'Developed',
    'PlowprintAnnualExpansion': 'Plowprint Annual Expansion',
  };

  const csvKey = Object.keys(keyOptions).find(csvKeyLookup => key.includes(csvKeyLookup));
  return keyOptions[csvKey];
};

export const getCSVCategory = key => {
  const categoryOptions = {
    'Quality_Low': 'Low',
    'Quality_Moderate': 'Moderate',
    'Quality_High': 'High',
    'Quality_VeryHigh': 'Very High',
    'Corn': 'Corn',
    'Wheat': 'Wheat',
    'Canola': 'Canola',
    'Soy': 'Soy',
    'OtherGrains': 'Other Grains',
    'OtherAgriculture': 'Other Crops',
    'GrasslandWetlandShrubland': 'Perennial',
    'IntactHabitat_Grassland': 'Grassland',
    'IntactHabitat_Shrubland': 'Shrubland',
    'IntactHabitat_Wetland': 'Wetland',
    'IntactHabitat_Other': 'Other',
    'Forest': 'Forest',
    'BarrenDeveloped': 'Barren/Developed',
    'CropFallow': 'Crop/Fallow',
    'StateLand': 'State Land',
    'FederalLand': 'Federal Land',
    'PrivateOther': 'Private/Other',
    'Tribal': 'Tribal',
  };

  const csvCategory = Object.keys(categoryOptions).find(categoryLookup => key.includes(categoryLookup));
  return csvCategory ? categoryOptions[csvCategory] : 'N/A';
};

export const getCSVYear = key => {
  const currentYear = new Date().getFullYear();

  const arrayOfYears = [];
  for (let y = 2009; y < currentYear; y++) {
    arrayOfYears.push(y);
  }

  return arrayOfYears.find(year => key.includes(year)) ? arrayOfYears.find(year => key.includes(year)) : 'N/A';
};

export const createSortOrder = () => {
  const annualCategories = [
    'Cumulative Plowprint N/A',
    'Intact Habitat Grassland',
    'Intact Habitat Forest',
    'Intact Habitat Shrubland',
    'Intact Habitat Wetland',
    'Intact Habitat Other',
    'Plowprint Annual Expansion Corn',
    'Plowprint Annual Expansion Wheat',
    'Plowprint Annual Expansion Soy',
    'Plowprint Annual Expansion Canola',
    'Plowprint Annual Expansion Other Grains',
    'Plowprint Annual Expansion Other Crops',
    'Intact Extent N/A',
    'Intact Ownership State Land',
    'Intact Ownership Federal Land',
    'Intact Ownership Private/Other',
    'Intact Ownership Tribal',
    'Plowprint Ownership State Land',
    'Plowprint Ownership Federal Land',
    'Plowprint Ownership Private/Other',
    'Plowprint Ownership Tribal',
    'Plowprint Composition Perennial',
    'Plowprint Composition Forest',
    'Plowprint Composition Barren/Developed',
    'Plowprint Composition Crop/Fallow',
  ];
  const nonAnnualCategories = [
    'Plowprint Expansion 2011 - 2018 Corn N/A',
    'Plowprint Expansion 2011 - 2018 Wheat N/A',
    'Plowprint Expansion 2011 - 2018 Canola N/A',
    'Plowprint Expansion 2011 - 2018 Soy N/A',
    'Plowprint Expansion 2011 - 2018 Other Grains N/A',
    'Plowprint Expansion 2011 - 2018 Other Crops N/A',
    'Plowprint Soil Quality Low N/A',
    'Plowprint Soil Quality Moderate N/A',
    'Plowprint Soil Quality High N/A',
    'Plowprint Soil Quality Very High N/A',
    'Intact Soil Quality Low N/A',
    'Intact Soil Quality Moderate N/A',
    'Intact Soil Quality High N/A',
    'Intact Soil Quality Very High N/A',
    'Open Water N/A N/A',
    'Developed N/A N/A'
  ];
  const sortDictionary = {};
  let sortPriority = 0;
  const maxYear = 2020;
  // Give the annualCategories a sort order and add it into the sortDictionary
  for (let category = 0; category < annualCategories.length; category++) {
    // For each category, we iterate by year and assign the sort priority
    const categoryName = annualCategories[category];
    const minYear = categoryName.includes('Plowprint Annual Expansion') ? 2010 : 2009;

    for (let year = minYear; year < maxYear; year++) {
      const keyName = `${categoryName} ${year}`;
      sortDictionary[keyName] = sortPriority;
      sortPriority++;
    }
  }

  // Give the nonAnnualCategories a sort order and add it into the sortDictionary
  for (let category = 0; category < nonAnnualCategories.length; category++) {
    // For each category assign it a value
    let keyName = nonAnnualCategories[category];
    if (keyName.includes('Plowprint Expansion')) {
      const primaryCategory = `${nonAnnualCategories[category].slice(0, 20)}`;
      const yearInsertion = `2011 - ${maxYear - 1}`;
      const subCategory = nonAnnualCategories[category].slice(31);
      keyName = `${primaryCategory}${yearInsertion}${subCategory}`;
    }
    sortDictionary[keyName] = sortPriority;
    sortPriority++;
  }

  return sortDictionary;
};

export const outfieldsToIgnore = [
  'Acres',
  'Acres2',
  'ACRES',
  'area_names',
  'Area_Num',
  'CEC',
  'County',
  'Country',
  'Dev',
  'Dev15',
  'DevPP',
  'Evans',
  'Focal',
  'FID_CEC_Gr',
  'GrPln',
  'H2O',
  'H2O15',
  'H2OPP',
  'HECTARES',
  'Id',
  'JV',
  'LEVEL3_NAM',
  'Link_ID',
  'MORB',
  'MSRB',
  'Name_1',
  'Name_Abr',
  'NGP',
  'NGPJV',
  'OBJECTID',
  'OPJV',
  'ORIG_FID',
  'PHJV',
  'PPP',
  'PPJV',
  'sq_mi',
  'SGP',
  'Shape',
  'Shape_Area',
  'Shape_Length',
  'STATE_NAME',
  'trans',
  'WWF_till'
];
